import { defineStore } from 'pinia'
import TextCell from '@/onenetwork/components/datatable/TextCell.vue'
import NumberCell from '@/onenetwork/components/datatable/NumberCell.vue'
import DateTimeCell from '@/onenetwork/components/datatable/DateTimeCell.vue'
import TagCell from '@/onenetwork/components/datatable/TagCell.vue'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { buildFilterObject } from '@/utils/filter'
import { markRaw } from 'vue'
import { BackofficeAPI } from '@/modules/backoffice/constants/backoffice.api.uri.const.ts'
import moment from 'moment'

// (Mock constants removed as we're switching to real APIs)



export interface tabItem {
  id: string,
  label: string,
  active: boolean,
}

export enum AttendanceActionTypes {
  FILTERING = 'FILTERING',
  PENDING= "PENDING",
  FETCHING= "FETCHING",
  FETCH_SUCCESS = "FETCH_SUCCESS",
  FETCH_FAILURE = "FETCH_FAILURE",
}

interface AttendanceStoreType {
  action_type: AttendanceActionTypes,
  action_mess: string,
  uuid: string,
  data: Object,
  filter: Array<{
    key: string,
    type: string,
    label: string,
    placeholder: string,
    value: any,
    options?: Array<{label: string, value: string}>
  }>,
  pageSize: number,
  page: number,
  tabs: Array<any>,
  tableConfig: any,
  selectedIds: Array<string|number>,
  matrixList: Array<any>,
  // === 3 API States ===
  dashboardData: any,
  dashboardLoading: boolean,
  dayDetail: any,
  dayDetailLoading: boolean,
}

export const useAttendanceReportStore = defineStore('backoffice.attendance', {
  state: (): AttendanceStoreType => ({
    action_type: AttendanceActionTypes.PENDING,
    action_mess: "",
    uuid: '',
    data: {
      list: [],
      totalRecords: 0,
      totalPage: 0,
    },
    filter: [
      {
        key: 'month',
        type: 'month-picker',
        label: 'Lọc theo tháng',
        placeholder: 'Chọn tháng',
        value: '',
      },
    ],
    pageSize: 20,
    page: 1,
    tabs: [
      {
        id: 'list',
        label: 'Danh sách chấm công',
        active: true,
      }
    ],
    tableConfig: {
      canShowHide: true,
      columns: {
        id: { label: 'ID', component: markRaw(TextCell), class: '', visible: true, sort: false, config: {} },
        started_at: { label: 'Ngày bắt đầu', component: markRaw(DateTimeCell), class: '', visible: true, sort: '', config: {} },
        ended_at: { label: 'Ngày kết thúc', component: markRaw(DateTimeCell), class: '', visible: true, sort: '', config: {} },
        status_text: { label: 'Trạng thái', component: markRaw(TagCell), class: '', visible: true, sort: false, config: {} },
        name: { label: 'Tên sự kiện', component: markRaw(TextCell), class: '', visible: true, sort: false, config: {} },
        total_items: { label: 'Số lượng mở bán', component: markRaw(NumberCell), class: 'justify-end', visible: true, sort: false, config: {} },
        created_at: { label: 'Ngày đăng ký', component: markRaw(DateTimeCell), class: '', visible: true, sort: 'desc', config: {} },
        created_by: { label: 'Người tạo', component: markRaw(TextCell), class: '', visible: true, sort: false, config: {} },
      }
    },
    selectedIds: [],
    matrixList: [],
    // === 3 API States ===
    dashboardData: null,
    dashboardLoading: false,
    dayDetail: null,
    dayDetailLoading: false,
  }),
  getters: {
    sortFilter: state => {
      const sort: any = []
      if(Object.keys(state.tableConfig?.columns).length>0){
        Object.keys(state.tableConfig?.columns).forEach(col => {
          if(state.tableConfig.columns[col].sort) {
            sort.push([col, state.tableConfig.columns[col].sort])
          }
        })
      }
      return sort
    }
  },
  actions: {
    //TAB ACTION
    changeTab (targetId: string) {

      for (const tab of this.tabs) {
        tab.active = (tab.id === targetId)
      }
    },
    closeTab (targetId: string) {
      const index = this.tabs.findIndex(tab => tab.id === targetId)
      if (index !== -1) {
        this.tabs.splice(index, 1)
      }
    },
    closeAllTab () {
      this.tabs = [{
        id: 'list',
        label: 'Danh sách FlashOffer',
        active: true,
      }]
    },
    newTab (item: tabItem) {
      const exists = this.tabs.some(tab => tab.id === item.id)
      if (!exists) {
        this.tabs.push(item)
      }
      this.changeTab(item.id)
    },

    //FILTER ACTION
    setFilter (filter: any) {
      this.filter = filter
      this.action_type = AttendanceActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    resetFilter () {
      this.filter = this.filter.map(item => {
        if (item.type === 'region-level-1' || item.type === 'region-level-2') {
          return {...item, value: '', options: []}
        }
        return {...item, value: ''}
      })
      this.page = 1
      this.action_type = AttendanceActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    changeFilter (key: string, value: any) {
      const filterItem = this.filter.find((item: {key: string}) => item.key === key)
      if (filterItem) {
        filterItem.value = value
        this.action_type = AttendanceActionTypes.FILTERING
        this.uuid = uuid.v4()
      }
    },
    putFilter(key: string, value: any) {
      this.filter = this.filter.map((field) => {
        if (field.key === key) {
          return { ...field, value: value }
        }
        return field
      })
    },

    //PAGE ACTION
    changePage (page: number) {
      this.page = page
      this.action_type = AttendanceActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePageSize (size: number) {
      this.pageSize = size
      this.action_type = AttendanceActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    //TABLE CONFIG ACTION
    toggleColumnVisibility(columnCode: string, visible: boolean) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].visible = visible
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },
    toggleSort(columnCode: string, sort: string) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].sort = sort
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },
    setSelectedRow(ids: Array<string|number>) {
      this.selectedIds = ids
    },
    //DATA ACTION
    async fetchList(){
      return new Promise(async (resolve, reject) => {
        try{
          this.action_type = AttendanceActionTypes.FETCHING
          const sort = this.sortFilter
          const pagination = {
            page: this.page,
            page_size: this.pageSize,
            sort: sort
          };

          console.log(210, this.filter, pagination);

          const filter = {
            ...pagination,
            month: this.filter[0].value ? moment(this.filter[0].value).format('YYYY-MM') : moment().format('YYYY-MM'),
          }
          console.log(217, filter)
          const res: any = await APIClient.get(BackofficeAPI.attendance.list, {
            params: filter
          })

          const { data } = res.data;

          // this.data = {
          //   list: formatTableDataFlashOffer(data.data) || [],
          //   totalRecords: data.total ?? 0,
          //   totalPage: data.last_page ?? 0
          // }
          this.action_type = AttendanceActionTypes.FETCH_SUCCESS
          resolve(data);
        } catch (e) {
          this.action_type = AttendanceActionTypes.FETCH_FAILURE
          reject(e)
        }
      })
    },

    // ==========================================
    // API 1: FETCH MATRIX (Bảng chấm công tổng)
    // GET /attendance/matrix?month=3&year=2026
    // ==========================================
    async fetchMatrixList(month?: number, year?: number) {
      try {
        this.action_type = AttendanceActionTypes.FETCHING;
        const res: any = await APIClient.get(BackofficeAPI.attendance.matrix, { 
          params: { month, year } 
        });
        // Linh hoạt xử lý cả res.data.data và res.data (nếu backend trả về array trực tiếp)
        this.matrixList = (res.data?.data || (Array.isArray(res.data) ? res.data : []));
        this.action_type = AttendanceActionTypes.FETCH_SUCCESS;
        return this.matrixList;
      } catch (e) {
        this.action_type = AttendanceActionTypes.FETCH_FAILURE;
        console.error('Fetch Matrix Error:', e);
        throw e;
      }
    },

    // ==========================================
    // API 2: FETCH DASHBOARD (KPI + Leaderboard + Trend)
    // GET /attendance/dashboard?month=3&year=2026
    // ==========================================
    async fetchDashboard(month?: number, year?: number) {
      try {
        this.dashboardLoading = true;
        const res: any = await APIClient.get(BackofficeAPI.attendance.dashboard, { 
          params: { month, year } 
        });
        // Linh hoạt xử lý object dashboard
        this.dashboardData = res.data?.data || res.data || null;
        this.dashboardLoading = false;
        return this.dashboardData;
      } catch (e) {
        this.dashboardLoading = false;
        console.error('Fetch Dashboard Error:', e);
        throw e;
      }
    },

    // ==========================================
    // API 3: FETCH DAY DETAIL (Chi tiết 1 ngày)
    // GET /attendance/detail?employee_id=1&day=15&month=3&year=2026
    // ==========================================
    async fetchDayDetail(employeeId: number, day: number, month?: number, year?: number) {
      try {
        this.dayDetailLoading = true;
        this.dayDetail = null;
        const res: any = await APIClient.get(BackofficeAPI.attendance.detail, { 
          params: { employee_id: employeeId, day, month, year } 
        });
        this.dayDetail = res.data?.data || res.data || null;
        this.dayDetailLoading = false;
        return this.dayDetail;
      } catch (e) {
        this.dayDetailLoading = false;
        console.error('Fetch Detail Error:', e);
        throw e;
      }
    },

    // HR Override (POST để lưu record mới)
    async updateHRRecord(employeeId: number, day: number, payload: any) {
      try {
        this.action_type = AttendanceActionTypes.FETCHING;
        const res: any = await APIClient.post(BackofficeAPI.attendance.list, { // Hoặc endpoint update riêng nếu có
          employee_id: employeeId,
          day,
          ...payload
        });
        this.action_type = AttendanceActionTypes.FETCH_SUCCESS;
        return res.data;
      } catch (e) {
        this.action_type = AttendanceActionTypes.FETCH_FAILURE;
        console.error('Update HR Record Error:', e);
        throw e;
      }
    }
  }
})
